import type { SegmentCondition, EmailEventType, Timeframe } from '@mailflow/core/segments';
import type { TreeContext } from './SegmentTree.js';

/**
 * Editor fuer eine einzelne Bedingung.
 *
 * Abgedeckt sind die Bedingungstypen, die im Alltag zaehlen: Verhalten,
 * Stammdatenfelder sowie Listen- und Tag-Zugehoerigkeit. Eigene Felder und
 * eigene Ereignisse unterstuetzt die API bereits, sie bekommen ihre
 * Oberflaeche zusammen mit den Custom Fields.
 */

const EVENT_OPTIONS: Array<{ value: EmailEventType; label: string }> = [
  { value: 'open', label: 'geöffnet' },
  { value: 'click', label: 'geklickt' },
  { value: 'delivered', label: 'zugestellt bekommen' },
  { value: 'sent', label: 'gesendet bekommen' },
  { value: 'reply', label: 'geantwortet' },
  { value: 'bounce_hard', label: 'dauerhaft unzustellbar' },
  { value: 'bounce_soft', label: 'vorübergehend unzustellbar' },
  { value: 'complaint', label: 'sich beschwert' },
  { value: 'unsubscribe', label: 'sich abgemeldet' },
];

const ATTRIBUTE_OPTIONS = [
  { value: 'status', label: 'Status' },
  { value: 'country', label: 'Land' },
  { value: 'city', label: 'Ort' },
  { value: 'email', label: 'E-Mail-Adresse' },
  { value: 'firstName', label: 'Vorname' },
  { value: 'lastName', label: 'Nachname' },
  { value: 'source', label: 'Quelle' },
  { value: 'leadScore', label: 'Lead-Score' },
  { value: 'lifecycleStage', label: 'Lebenszyklus' },
  { value: 'consentStatus', label: 'Einwilligung' },
] as const;

const ATTRIBUTE_OPS = [
  { value: 'equals', label: 'ist' },
  { value: 'not_equals', label: 'ist nicht' },
  { value: 'contains', label: 'enthält' },
  { value: 'starts_with', label: 'beginnt mit' },
  { value: 'gte', label: 'ist mindestens' },
  { value: 'lte', label: 'ist höchstens' },
  { value: 'is_empty', label: 'ist leer' },
  { value: 'is_not_empty', label: 'ist ausgefüllt' },
] as const;

type Kind = 'email_event' | 'attribute' | 'list_member' | 'tag';

export function ConditionEditor({
  condition,
  context,
  onChange,
}: {
  condition: SegmentCondition;
  context: TreeContext;
  onChange: (next: SegmentCondition) => void;
}) {
  function changeKind(kind: Kind) {
    switch (kind) {
      case 'email_event':
        onChange({ type: 'email_event', event: 'open' });
        return;
      case 'attribute':
        onChange({ type: 'attribute', field: 'status', op: 'equals', value: 'active' });
        return;
      case 'list_member':
        onChange({ type: 'list_member', listId: context.lists[0]?.id ?? '' });
        return;
      case 'tag':
        onChange({ type: 'tag', tagId: context.tags[0]?.id ?? '' });
        return;
    }
  }

  const kind = (['email_event', 'attribute', 'list_member', 'tag'] as const).includes(
    condition.type as Kind,
  )
    ? (condition.type as Kind)
    : 'email_event';

  return (
    <div className="rule__fields">
      <select
        className="input input--compact"
        value={kind}
        onChange={(e) => changeKind(e.target.value as Kind)}
        aria-label="Art der Bedingung"
      >
        <option value="email_event">Verhalten</option>
        <option value="attribute">Feld</option>
        <option value="list_member">Liste</option>
        <option value="tag">Tag</option>
      </select>

      {condition.type === 'email_event' && (
        <EmailEventFields condition={condition} context={context} onChange={onChange} />
      )}

      {condition.type === 'attribute' && (
        <>
          <select
            className="input input--compact"
            value={condition.field}
            onChange={(e) => onChange({ ...condition, field: e.target.value as 'status' })}
            aria-label="Feld"
          >
            {ATTRIBUTE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            className="input input--compact"
            value={condition.op}
            onChange={(e) => onChange({ ...condition, op: e.target.value as 'equals' })}
            aria-label="Vergleich"
          >
            {ATTRIBUTE_OPS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {condition.op !== 'is_empty' && condition.op !== 'is_not_empty' && (
            <input
              className="input input--compact"
              value={String(condition.value ?? '')}
              onChange={(e) => onChange({ ...condition, value: e.target.value })}
              placeholder="Wert"
              aria-label="Wert"
            />
          )}
        </>
      )}

      {condition.type === 'list_member' && (
        <>
          <NegateToggle
            negate={condition.negate === true}
            onChange={(negate) => onChange({ ...condition, negate })}
            labels={['ist in', 'ist nicht in']}
          />
          <select
            className="input input--compact"
            value={condition.listId}
            onChange={(e) => onChange({ ...condition, listId: e.target.value })}
            aria-label="Liste"
          >
            {context.lists.length === 0 && <option value="">Keine Liste vorhanden</option>}
            {context.lists.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </>
      )}

      {condition.type === 'tag' && (
        <>
          <NegateToggle
            negate={condition.negate === true}
            onChange={(negate) => onChange({ ...condition, negate })}
            labels={['hat', 'hat nicht']}
          />
          <select
            className="input input--compact"
            value={condition.tagId}
            onChange={(e) => onChange({ ...condition, tagId: e.target.value })}
            aria-label="Tag"
          >
            {context.tags.length === 0 && <option value="">Kein Tag vorhanden</option>}
            {context.tags.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </>
      )}
    </div>
  );
}

function EmailEventFields({
  condition,
  context,
  onChange,
}: {
  condition: Extract<SegmentCondition, { type: 'email_event' }>;
  context: TreeContext;
  onChange: (next: SegmentCondition) => void;
}) {
  const timeframe = condition.timeframe;
  const days = timeframe?.kind === 'last' ? timeframe.value : '';

  function setDays(raw: string) {
    if (raw === '') {
      const { timeframe: _drop, ...rest } = condition;
      onChange(rest);
      return;
    }
    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return;
    const next: Timeframe = { kind: 'last', unit: 'days', value };
    onChange({ ...condition, timeframe: next });
  }

  return (
    <>
      {/* Die Negation ist der wichtigste Schalter der ganzen Oberflaeche. */}
      <NegateToggle
        negate={condition.negate === true}
        onChange={(negate) => onChange({ ...condition, negate })}
        labels={['hat', 'hat nicht']}
      />

      <select
        className="input input--compact"
        value={condition.event}
        onChange={(e) => onChange({ ...condition, event: e.target.value as EmailEventType })}
        aria-label="Ereignis"
      >
        {EVENT_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>

      {/* Kampagnen-Auswahl fuer praezise Segmentierung */}
      <select
        className="input input--compact"
        value={condition.scope?.campaignId ?? ''}
        onChange={(e) => {
          const campaignId = e.target.value;
          onChange({
            ...condition,
            scope: campaignId ? { ...condition.scope, campaignId } : undefined,
          });
        }}
        aria-label="Kampagne filtern"
      >
        <option value="">Alle Kampagnen</option>
        {(context.campaigns ?? []).map((c) => (
          <option key={c.id} value={c.id}>
            Kampagne: {c.name}
          </option>
        ))}
      </select>

      <span className="rule__label">in den letzten</span>
      <input
        className="input input--tiny"
        type="number"
        min={1}
        value={days}
        onChange={(e) => setDays(e.target.value)}
        placeholder="∞"
        aria-label="Anzahl Tage"
      />
      <span className="rule__label">Tagen</span>

      <label className="rule__check">
        <input
          type="checkbox"
          checked={condition.excludeMachine !== false}
          onChange={(e) => onChange({ ...condition, excludeMachine: e.target.checked })}
        />
        Bot-Öffnungen ausschließen
      </label>
    </>
  );
}

function NegateToggle({
  negate,
  onChange,
  labels,
}: {
  negate: boolean;
  onChange: (negate: boolean) => void;
  labels: [string, string];
}) {
  return (
    <div className="negate-toggle" role="group" aria-label="Bejahen oder verneinen">
      <button type="button" data-active={!negate} onClick={() => onChange(false)}>
        {labels[0]}
      </button>
      <button type="button" data-active={negate} onClick={() => onChange(true)}>
        {labels[1]}
      </button>
    </div>
  );
}
